import type { RowType } from '../report-configurations/reportConfigurationsTypes';
import { PRIMARY_RANGE_ID } from '../report-configurations/reportConfigurationsTypes';
import { resolveComparisonRangeDates } from '../report-configurations/utils/comparisonRanges';
import type { ComparisonColumnSource } from './comparisonColumnModel';
import {
  DEFAULT_COMPARISON_COLUMN_SOURCE,
  findCellForColumn,
  getColumnGroups,
  getComparisonColumnGroupsForSource,
  getComparisonRangeHeaders,
  getTotalColumnGroup,
  isChartableComparisonColumn,
  isComparisonReport,
  resolveComparisonRangeHeader,
} from './comparisonColumnModel';
import type { Report, ReportColumn, ReportRow } from './generatedReportsTypes';

// ================================================================================================
// Comparison chart model
//
// Turns a COMPARISON report into the rows and ranges a per-category comparison bar chart draws:
// one bar per range per category, plus the delta between two of them.
//
// Two things make this a separate module from `comparisonColumnModel` rather than more functions
// inside it. It aggregates rather than describes — it reads cell *values*, not just column
// metadata — and it makes presentation-adjacent decisions (which rows are chartable, how the
// sections split, what counts as an increase) that the grid does not share.
//
// Everything here is pure and takes the report as an argument, so the web and mobile layers can
// call it directly or through the memoized `getComparisonChartData` selector.
//
// Drilling in re-levels the chart rather than only opening the register: the caller passes the
// drill-down breadcrumbs and gets the clicked row's children back, one level per crumb — the same
// contract `getPieChartDataWithDrillDown` implements for the pie. That selector cannot be reused
// here because it short-circuits on any comparison report by design.
//
// The chart reads exactly one selected column group: one bar per configured range. PERIOD is the
// client default; callers may explicitly select TOTAL or a particular period group.
// ================================================================================================

/**
 * Rows that exist only to group the ones beneath them. QCS sends them as ordinary rows carrying
 * summed values, so charting them would double-count every category under them and swamp the scale.
 *
 * `PERSONAL` / `BUSINESS` and the `EXPENSE_*` / `INCOME_*` pairs are the intermediate tier a
 * business-entitled account gets. They are wrappers in exactly the same sense, which is why a
 * single-level `parentId` check is not enough to find the real leaves.
 */
export const REPORT_WRAPPER_ENTITY_IDS = new Set([
  'EXPENSE',
  'INCOME',
  'PERSONAL',
  'BUSINESS',
  'EXPENSE_BUSINESS',
  'EXPENSE_PERSONAL',
  'INCOME_BUSINESS',
  'INCOME_PERSONAL',
]);

/** The sentinel `parentId` the server puts on a root row. `rowsByParentId['-1']` throughout web. */
const ROOT_PARENT_ID = '-1';

export type ComparisonExpectedSign = 'POSITIVE' | 'NEGATIVE' | 'ANY';

/**
 * How a row's current period compares with the range it is measured against.
 *
 * `new_spend` and `no_spend_yet` are not "increase by 100%" and "decrease by 100%": one has no
 * ratio to report because the base was zero, and the other is a category that has gone quiet. Both
 * need their own affordance rather than a misleading percentage.
 */
export type ComparisonDeltaKind = 'increase' | 'decrease' | 'unchanged' | 'new_spend' | 'no_spend_yet';

export type ComparisonChartRange = {
  /** The selected group's column this range's values come from. The key for cell lookup. */
  columnId: string;
  rangeId: string;
  isPrimary: boolean;
  /**
   * The range's display label, as the server named the period it covers ("Dec 1, 2025 - Dec 31,
   * 2025"). Read from the range's PERIOD columns, since a TOTAL-group column is headed "Total".
   * Empty when the server sent no name — a range that has ended carries a blank header, and blank is
   * the contract rather than a value to invent one for.
   */
  label: string;
  /**
   * The range's own dates, from its PERIOD columns' date headers (widened to their union if the
   * report has several periods). Undefined only if the server sent no date header for the range.
   *
   * Carried so a bar click can open the register scoped to the *clicked* range rather than to the
   * report's primary range — which is the whole point of drilling into a comparison bar.
   */
  startOn?: string;
  endOn?: string;
};

export type ComparisonChartValue = {
  columnId: string;
  rangeId: string;
  /**
   * `null` means the report has no cell for this (row, range) at all — a range that ended before
   * the row existed. Distinct from `0`, which is a real "nothing happened" the chart draws a stub
   * for.
   */
  value: number | null;
  /**
   * Legacy nested deltas from a VALUE cell. New responses provide dedicated DELTA_AMOUNT and
   * DELTA_PERCENTAGE columns instead; these remain only as a fallback when those columns do not
   * exist. `undefined` is absent and `null` is explicitly supplied by QCS.
   */
  serverDeltaAmount: number | null | undefined;
  serverDeltaPercentage: number | null | undefined;
};

/**
 * One ancestry entry for the drill-down breadcrumb, shaped like the web layer's `NameData` so a
 * wrapper can hand it straight to the drill-down dispatch. Mirrors `buildSankeyData`'s
 * `SankeyNameEntry` for the same reason.
 */
export type ComparisonChartName = {
  entities: string[];
  entityId: string;
  rowId: string;
  name: string;
  displayName: string;
};

export type ComparisonChartRow = {
  id: string;
  parentId?: string;
  entityId?: string;
  /** Whether this row has report children and therefore remains a drill-down entry at zero. */
  hasChildren?: boolean;
  name: string;
  /**
   * The row's ancestry, outermost first, with the income/expense wrappers excluded — they are
   * section boundaries here, and the register's breadcrumb filters them out by name anyway.
   *
   * Present so a bar click can drill down without the caller re-walking `report.rows`.
   */
  names: ComparisonChartName[];
  expectedSign: ComparisonExpectedSign;
  /** One entry per range, in `ComparisonChartData.ranges` order. Always the same length. */
  values: ComparisonChartValue[];
  deltaKind: ComparisonDeltaKind;
  /** QCS's signed arithmetic amount, or the locally derived arithmetic amount for synthetic rows. */
  deltaAmount: number | null | undefined;
  /**
   * Compatibility alias for `deltaAmount`. Both preserve the arithmetic sign; direction lives in
   * `deltaKind` and is derived from the period values rather than inferred from the server delta.
   */
  deltaAmountSigned: number | null | undefined;
  /** QCS's signed arithmetic percentage. Formatting may hide its sign when an arrow carries it. */
  deltaPercent: number | null | undefined;
};

export type ComparisonChartSectionKey = 'INCOME' | 'EXPENSE' | 'SINGLE';

export type ComparisonChartSection = {
  key: ComparisonChartSectionKey;
  label: string;
  expectedSign: ComparisonExpectedSign;
  rows: ComparisonChartRow[];
  /**
   * The bar scale for this section, over every row and every range — not just the visible ones.
   *
   * Per section, so a $50 income category is not rendered invisible beside a $5,000 mortgage. Over
   * *all* rows, so expanding past the truncation limit adds bars without rescaling the ones already
   * on screen.
   */
  maxAbsValue: number;
};

/**
 * The whole report's totals, one per range — what the chart header shows above the card.
 *
 * Separate from the sections because it is a property of the *report*, not of the level being
 * charted: drilling into a category re-levels `sections` but must leave the header alone.
 *
 * It exists at all because `Report.total` is a single server-supplied scalar with no range attached,
 * so on a comparison report it can only ever be right for one range at best — and today is right for
 * none. Deriving both figures from the selected column group keeps the header and bars aligned.
 */
export type ComparisonChartTotals = {
  /** One entry per range, in `ranges` order. `null` when the range has no cell at all. */
  values: Array<number | null>;
  expectedSign: ComparisonExpectedSign;
  deltaKind: ComparisonDeltaKind;
  /** Signed arithmetic amount. See `ComparisonChartRow.deltaAmount`. */
  deltaAmount: number | null | undefined;
  /** The arithmetic difference, sign intact. See `ComparisonChartRow.deltaAmountSigned`. */
  deltaAmountSigned: number | null | undefined;
  deltaPercent: number | null | undefined;
};

export const EMPTY_COMPARISON_CHART_TOTALS: ComparisonChartTotals = {
  values: [],
  expectedSign: 'ANY',
  deltaKind: 'unchanged',
  deltaAmount: null,
  deltaAmountSigned: null,
  deltaPercent: null,
};

/**
 * One section's totals, carrying the section it belongs to.
 *
 * `ComparisonChartTotals` is the *report's* net figure — income and expenses already summed — so it
 * can say what the bottom line did but not which half moved. The mobile header card shows three
 * blocks (Income, Expenses, Net income), and deriving the first two in the view would mean summing
 * charted rows, which is wrong twice over: it drops rows past the truncation limit, and it re-levels
 * on drill-down, so the header would rewrite itself to whatever category the user opened.
 */
export type ComparisonChartSectionTotals = ComparisonChartTotals & {
  key: ComparisonChartSectionKey;
  /** The wrapper row's own name, so an entitled report's wording carries through. */
  label: string;
};

export type ComparisonChartDiagnosticReason =
  | 'NOT_COMPARISON'
  | 'NO_PERIOD_GROUP'
  | 'NO_TOTAL_GROUP'
  | 'NO_RANGES'
  | 'NO_ROWS';

export type ComparisonChartData = {
  isComparison: boolean;
  ranges: ComparisonChartRange[];
  sections: ComparisonChartSection[];
  /** The report-level figures for the header. Unaffected by drill-down. */
  totals: ComparisonChartTotals;
  /**
   * The same figures split by section, in INCOME, EXPENSE order. Empty on an unsectioned report,
   * where the report total is the only total there is. Unaffected by drill-down, like `totals`.
   */
  sectionTotals: ComparisonChartSectionTotals[];
  diagnostics: {
    /** Why the result is empty, when it is. Absent on a populated result. */
    reason?: ComparisonChartDiagnosticReason;
    usedUntypedColumnFallback: boolean;
    comparisonColumnSource?: ComparisonColumnSource;
    columnGroupId?: string;
  };
};

const emptyData = (reason: ComparisonChartDiagnosticReason, isComparison = true): ComparisonChartData => ({
  isComparison,
  ranges: [],
  sections: [],
  totals: EMPTY_COMPARISON_CHART_TOTALS,
  sectionTotals: [],
  diagnostics: { reason, usedUntypedColumnFallback: false },
});

const isMissingNumber = (value: string | number | null | undefined): boolean => value == null || value === '';

/** Cell values arrive as strings. Anything unparseable is absent rather than silently zero. */
const toNumber = (value: string | number | null | undefined): number | null => {
  if (isMissingNumber(value)) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Two decimals, matching the design's `40.12%` and the grid's amount precision. A single decimal
 * would render the mock's percentages as `40.1%`, and the delta chip sits directly beside the grid.
 */
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * A server-supplied delta, preserving the difference between the two absent-ish states.
 *
 * `undefined` means the field was not sent. `null` means the server sent it explicitly as null,
 * which on `deltaPercentage` is meaningful: the comparison value was zero, so there is no ratio.
 * Collapsing them would lose the only signal distinguishing "no data" from "no ratio".
 */
const toServerNumber = (value: string | number | null | undefined): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return toNumber(value);
};

/**
 * The direction a row is expected to move, from the server's own hint.
 *
 * Used for the unsectioned row types, where there is no wrapper to inherit a direction from. `ANY`
 * leaves values unflipped, so a row the server says nothing about is compared arithmetically.
 */
const rowExpectedSign = (row: ReportRow): ComparisonExpectedSign => row.expectedSign ?? 'ANY';

/**
 * The one chartable column per configured range, in server order.
 *
 * `isChartableComparisonColumn` accepts a nullish type, and that leniency sweeps the derived
 * DELTA_AMOUNT / DELTA_PERCENTAGE columns in with the values — they carry the `rangeId` of the range
 * they measure against, so a nullish-typed payload would turn each into an extra hatched bar and an
 * extra tooltip line for a range that already has one. The requirement is one raw-value bar per
 * range, so the first column claiming a `rangeId` wins.
 *
 * First is the right one in both payload shapes: an explicitly typed response is filtered to VALUE
 * columns before this runs, and an untyped one still lists a range's value column ahead of the
 * columns derived from it.
 */
const oneColumnPerRange = (columns: ReportColumn[]): ReportColumn[] => {
  const seen = new Set<string>();
  return columns.filter(isChartableComparisonColumn).filter((column) => {
    const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
    if (seen.has(rangeId)) return false;
    seen.add(rangeId);
    return true;
  });
};

type ComparisonDeltaColumns = {
  amount?: ReportColumn;
  percentage?: ReportColumn;
};

/** Dedicated QCS delta columns for the range compared with the primary range. */
const findComparisonDeltaColumns = (columns: ReportColumn[], rangeId: string | undefined): ComparisonDeltaColumns => ({
  amount: columns.find((column) => column.rangeId === rangeId && column.comparisonColumnType === 'DELTA_AMOUNT'),
  percentage: columns.find(
    (column) => column.rangeId === rangeId && column.comparisonColumnType === 'DELTA_PERCENTAGE',
  ),
});

/** Read a dedicated delta cell without collapsing absent, explicit null, and zero. */
const readDedicatedDelta = (row: ReportRow, column: ReportColumn | undefined): number | null | undefined =>
  column ? toServerNumber(findCellForColumn(row, column.id, true)?.value) : undefined;

/**
 * Normalise a value to a magnitude in the direction the row is *expected* to move.
 *
 * QCS sends expenses as negative numbers, so an expense going from -100 to -150 is arithmetically a
 * decrease while being plainly an increase in spending. Flipping by `expectedSign` first means the
 * rest of the delta logic works in one direction and the chip says what a person would say.
 */
const toMagnitude = (value: number, expectedSign: ComparisonExpectedSign): number =>
  expectedSign === 'NEGATIVE' ? -value : value;

/**
 * Which range the Difference column measures the current period against.
 *
 * A constant rather than a parameter, and exported so the chart's collapsed-list rollup compares
 * against the same range these row deltas do. The comparison picker models exactly one comparison
 * range today; when it grows to let the user pick among several, this becomes the selected index and
 * has to be threaded through *both* callers at once — a per-caller override silently makes the real
 * rows and the aggregate row in one column describe different ranges.
 */
export const COMPARISON_DELTA_RANGE_INDEX = 1;

/**
 * The delta between a row's current period and the range it is compared against.
 *
 * Dedicated QCS delta cells remain authoritative for real rows and server-owned totals. This
 * helper covers legacy payloads and genuinely synthetic aggregates, such as "Everything else",
 * where no server delta cell exists.
 *
 * Amount and percentage preserve the arithmetic sign of `current - compare`. Direction is a
 * separate concern: `deltaKind` describes whether the category moved up or down after accounting
 * for `expectedSign`. Keeping those concepts separate is the SIMPL-35539 contract.
 */
export const computeComparisonDelta = (
  current: number | null,
  compare: number | null,
  expectedSign: ComparisonExpectedSign,
): { amount: number | null; signedAmount: number | null; percent: number | null; kind: ComparisonDeltaKind } => {
  // Nothing on either side is the only case with no movement to report.
  if (current == null && compare == null) {
    return { amount: null, signedAmount: null, percent: null, kind: 'unchanged' };
  }

  // An absent cell is not a reported zero, but the row still moved by whatever the other side
  // holds, and the grid shows that movement in its own Difference ($) column — so the delta is computed
  // as if the missing side were zero rather than withheld. What an absent side does *not* earn is
  // the named zero-base states below: those states require a reported zero rather than a missing
  // figure. The presentation layer still renders their absolute difference and direction.
  const eitherAbsent = current == null || compare == null;
  const now = toMagnitude(current ?? 0, expectedSign);
  const before = toMagnitude(compare ?? 0, expectedSign);

  // Undo the `expectedSign` flip to recover the plain arithmetic difference. This is equivalent to
  // `current - compare` in every branch below, including the zero-base ones.
  const signed = (magnitudeDelta: number): number => toMagnitude(magnitudeDelta, expectedSign);

  const amount = now - before;
  if (amount === 0) {
    return { amount: 0, signedAmount: 0, percent: 0, kind: 'unchanged' };
  }

  // No base to divide by. "New" is the honest label; a percentage would be infinite.
  if (before === 0 && !eitherAbsent) {
    const signedAmount = signed(now);
    return { amount: signedAmount, signedAmount, percent: null, kind: 'new_spend' };
  }

  if (now === 0 && !eitherAbsent) {
    const signedAmount = signed(-before);
    return { amount: signedAmount, signedAmount, percent: signedAmount < 0 ? -100 : 100, kind: 'no_spend_yet' };
  }

  const signedAmount = signed(amount);
  return {
    amount: signedAmount,
    signedAmount,
    // A zero base is only reachable here with a side absent, and it is still not divisible.
    percent: before === 0 ? null : round2((signedAmount / Math.abs(before)) * 100),
    kind: amount > 0 ? 'increase' : 'decrease',
  };
};

/**
 * Combine authoritative server display values with client-derived direction.
 *
 * QCS DELTA_AMOUNT and DELTA_PERCENTAGE values are signed arithmetic differences. They pass
 * through unchanged, including explicit nulls. `expectedSign` is deliberately not applied to
 * either value; it only affects the movement derived by `computeComparisonDelta` from the two
 * period values. This separation is the SIMPL-35539 contract: the server owns the math shown while
 * the client owns the arrow and favorable/unfavorable intent.
 *
 * Undefined means the server supplied no delta cell. Legacy nested deltas and synthetic aggregates
 * may opt into a period-derived fallback; dedicated delta columns preserve absence as undefined.
 * Explicit null remains null.
 */
export const serverComparisonDelta = (
  current: number | null,
  compare: number | null,
  serverAmount: number | null | undefined,
  serverPercent: number | null | undefined,
  expectedSign: ComparisonExpectedSign,
  fallbackAmountWhenAbsent = true,
  fallbackPercentWhenAbsent = true,
): {
  amount: number | null | undefined;
  signedAmount: number | null | undefined;
  percent: number | null | undefined;
  kind: ComparisonDeltaKind;
} => {
  const derived = computeComparisonDelta(current, compare, expectedSign);
  let amount = serverAmount;
  if (amount === undefined && fallbackAmountWhenAbsent) amount = derived.signedAmount;

  let percent = serverPercent;
  if (percent === undefined && fallbackPercentWhenAbsent) percent = derived.percent;

  return { amount, signedAmount: amount, percent, kind: derived.kind };
};

const isWrapperRow = (row: ReportRow): boolean => !!row.entityId && REPORT_WRAPPER_ENTITY_IDS.has(row.entityId);

/**
 * The response's own grand-total row, sent as an ordinary root row rather than flagged `isTotal`.
 *
 * Every other consumer filters it out by hand — the grid skips it for SPENDING and INCOME
 * (`RowColumnBuilders.js`), the bar selector's `meaningfulTopRows` and the pie's
 * `hasChartableRowData` both exclude it, and `useZeroState` ignores it when deciding emptiness.
 * Left in, it is the largest row in the report by construction: it would draw a bar equal to the
 * sum of all the others, own the scale, and be counted twice in the header's total.
 */
const isTotalEntityRow = (row: ReportRow): boolean => row.entityId === 'TOTAL' || row.entityType === 'TOTAL';

/** A row that carries no data of its own and must never become a bar. */
const isSkippableRow = (row: ReportRow): boolean =>
  !!row.isTotal || !!row.isExcludedFromReports || isWrapperRow(row) || isTotalEntityRow(row);

type SectionAssignment = { key: ComparisonChartSectionKey; label: string; expectedSign: ComparisonExpectedSign };

const DEFAULT_SECTION_LABELS: Record<'INCOME' | 'EXPENSE', string> = {
  INCOME: 'Income',
  EXPENSE: 'Expenses',
};

/**
 * Which of the two NET_INCOME sections a row belongs to, by walking its ancestors.
 *
 * Walks the whole chain rather than checking the immediate parent, because a business-entitled
 * account nests categories under `EXPENSE > EXPENSE_PERSONAL > …`. `PERSONAL` and `BUSINESS` name a
 * *ledger*, not a direction, so the walk continues through them.
 *
 * It keeps walking after the first match and returns the **outermost** direction wrapper, which is
 * what makes a business-entitled report render two sections rather than four: `EXPENSE_PERSONAL` and
 * `EXPENSE_BUSINESS` both resolve up to the single `EXPENSE`, so their categories share one section
 * and one bar scale. Returning the first match instead would label the section "Personal Expenses"
 * and give business expenses a separate scale of their own.
 *
 * Depth-guarded: a malformed response with a parent cycle would otherwise hang the render.
 */
const findSection = (row: ReportRow, rowsById: Map<string, ReportRow>): SectionAssignment | undefined => {
  let current: ReportRow | undefined = row;
  let depth = 0;
  let outermost: SectionAssignment | undefined;

  while (current && depth < 32) {
    const entityId = current.entityId ?? '';
    const key: 'INCOME' | 'EXPENSE' | undefined = (() => {
      if (entityId === 'INCOME' || entityId.startsWith('INCOME_')) return 'INCOME';
      if (entityId === 'EXPENSE' || entityId.startsWith('EXPENSE_')) return 'EXPENSE';
      return undefined;
    })();

    if (key) {
      outermost = {
        key,
        label: current.name ?? DEFAULT_SECTION_LABELS[key],
        expectedSign: key === 'INCOME' ? 'POSITIVE' : 'NEGATIVE',
      };
    }

    current = current.parentId ? rowsById.get(current.parentId) : undefined;
    depth += 1;
  }

  return outermost;
};

/**
 * The rows the chart draws: the outermost level that holds real data.
 *
 * For a CATEGORY report that is the level just inside the wrappers — the top-level categories,
 * whose own subcategories roll up into them. Note this is the *inverse* of `getTopLevelPieRows`,
 * which descends past the wrappers to find leaves; here the wrappers are the section boundaries and
 * their children are the bars.
 *
 * For every other row type there are no wrappers at all, so the chartable rows are simply the ones
 * with no parent.
 */
const isChartableRow = (row: ReportRow, rowsById: Map<string, ReportRow>): boolean => {
  if (isSkippableRow(row)) return false;
  if (!row.parentId) return true;
  const parent = rowsById.get(row.parentId);
  // A parent that is itself absent means this row is effectively top-level.
  return !parent || isWrapperRow(parent);
};

/**
 * The ancestry a drill-down breadcrumb needs, outermost first.
 *
 * Wrapper rows are dropped rather than stopped at: a business-entitled report nests categories
 * under `EXPENSE > EXPENSE_PERSONAL`, and neither belongs in a breadcrumb. Depth-guarded against a
 * malformed parent cycle, like `findSection`.
 */
const buildRowNames = (row: ReportRow, rowsById: Map<string, ReportRow>): ComparisonChartName[] => {
  const chain: ReportRow[] = [];
  let cursor: ReportRow | undefined = row;
  let depth = 0;

  while (cursor && depth < 32) {
    if (!isWrapperRow(cursor)) chain.unshift(cursor);
    cursor = cursor.parentId ? rowsById.get(cursor.parentId) : undefined;
    depth += 1;
  }

  return chain.map((entry) => {
    const entityId = entry.entityId ?? '';
    const name = entry.name ?? '';
    return {
      entities: [entityId],
      entityId,
      rowId: String(entry.id ?? entityId),
      name,
      displayName: name,
    };
  });
};

type AccountUsageLabel = 'Business' | 'Personal';

/** The ledger an account row belongs to, inferred from the server's wrapper ancestry. */
const findAccountUsageLabel = (row: ReportRow, rowsById: Map<string, ReportRow>): AccountUsageLabel | undefined => {
  let current: ReportRow | undefined = row;
  let depth = 0;

  while (current && depth < 32) {
    const entityId = current.entityId ?? '';
    if (entityId === 'BUSINESS' || entityId.endsWith('_BUSINESS')) return 'Business';
    if (entityId === 'PERSONAL' || entityId.endsWith('_PERSONAL')) return 'Personal';
    current = current.parentId ? rowsById.get(current.parentId) : undefined;
    depth += 1;
  }

  return undefined;
};

/**
 * One entry of the chart's drill-down path.
 *
 * Structurally a subset of the web layer's `Breadcrumb` (`id` is the report row id, `entityId` its
 * `categoryId`), so a caller can hand its breadcrumbs straight in without a translation layer of
 * its own. Only the fields that identify a row are read.
 */
export type ComparisonChartCrumb = {
  id?: string;
  entityId?: string;
  name?: string;
};

export type ToComparisonChartDataOptions = {
  /**
   * The drill-down path, outermost first, including any UI-only root crumb ("All categories") —
   * crumbs that identify no row are skipped rather than ending the walk.
   */
  breadcrumbs?: readonly ComparisonChartCrumb[];
  /** The typed group whose column ids and cells drive the chart. */
  comparisonColumnSource?: ComparisonColumnSource;
  /** Selects one PERIOD group when a bucketed report contains several. */
  periodGroupId?: string;
};

/**
 * One PERIOD group's header figures, both additive and running. Totals only — no rows — because the
 * over-time model reads every period and needs nothing per row.
 */
export type ComparisonPeriodTotals = Pick<ComparisonChartData, 'ranges' | 'totals' | 'sectionTotals'> & {
  /** Running totals through this period, aligned to `ranges`. */
  cumulativeTotals: ComparisonChartTotals;
  /** Running section totals aligned to `sectionTotals`. */
  cumulativeSectionTotals: ComparisonChartSectionTotals[];
};

const EMPTY_CRUMBS: readonly ComparisonChartCrumb[] = [];

/**
 * The row a crumb names, by the same priority the pie's drill-down uses:
 * 4 = id + entityId, 3 = id, 2 = entityId, 1 = name.
 *
 * Deliberately the same ladder as `getPieChartDataWithDrillDown`, because the breadcrumbs come from
 * the same place and carry the same fields — a comparison drill-down that resolved crumbs
 * differently would land on a different row than the pie does for the identical click.
 */
const matchCrumbRow = (rows: ReportRow[], crumb: ComparisonChartCrumb): ReportRow | undefined =>
  rows.reduce<{ row: ReportRow | undefined; priority: number }>(
    (best, row) => {
      const rowId = row.id == null ? undefined : String(row.id);
      let priority = 0;

      if (crumb.id && crumb.entityId && rowId === crumb.id && row.entityId === crumb.entityId) {
        priority = 4;
      } else if (crumb.id && rowId === crumb.id) {
        priority = 3;
      } else if (crumb.entityId && row.entityId === crumb.entityId) {
        priority = 2;
      } else if (!crumb.entityId && crumb.name && row.name === crumb.name) {
        priority = 1;
      }

      // First row wins a tie, keeping the server's ordering stable.
      return priority > best.priority ? { row, priority } : best;
    },
    { row: undefined, priority: 0 },
  ).row;

/**
 * The level the chart draws, walking one step down per breadcrumb.
 *
 * Mirrors the pie's drill-down exactly: a crumb whose row has usable children re-levels the chart to
 * those children, and a crumb naming a leaf re-levels to that row alone — so drilling into a leaf
 * still narrows the chart to the thing the register is now showing, rather than leaving the chart
 * where it was.
 *
 * Crumbs that identify no row are skipped, not fatal: the root crumb the UI prepends
 * ("All categories") carries only a label, and a wrapper crumb names a row this model never charts.
 * A crumb that should have matched but does not stops the walk, leaving the last level that resolved.
 */
const resolveLevelRows = (
  topRows: ReportRow[],
  childrenByParentId: Map<string, ReportRow[]>,
  breadcrumbs: readonly ComparisonChartCrumb[],
): ReportRow[] => {
  let currentRows = topRows;

  for (const crumb of breadcrumbs) {
    const identifies = !!(crumb.id || crumb.entityId);
    if (!identifies || (crumb.entityId && REPORT_WRAPPER_ENTITY_IDS.has(crumb.entityId))) {
      continue;
    }

    const target = matchCrumbRow(currentRows, crumb);
    if (!target) break;

    const children = (childrenByParentId.get(String(target.id ?? '')) ?? []).filter((row) => !isSkippableRow(row));
    currentRows = children.length > 0 ? children : [target];
  }

  return currentRows;
};

/**
 * The direction the report as a whole is expected to move, for the header's delta.
 *
 * Read from the sections the report actually has, not from the mere presence of a wrapper row: a
 * spending report is wrapped in `EXPENSE` too, and calling that net income made the header point ↑
 * on spending that had fallen while the grid's own change column beside it pointed ↓. Only a report
 * carrying *both* directions is a net figure, where up is unambiguously good news.
 *
 * Unsectioned reports fall back to unanimity among their rows — a payee report of expenses inherits
 * their direction, and one mixing directions has no single "up is bad" reading, so it is compared
 * arithmetically.
 */
const unanimousExpectedSign = (signs: ComparisonExpectedSign[]): ComparisonExpectedSign => {
  const first = signs[0] ?? 'ANY';
  return signs.every((sign) => sign === first) ? first : 'ANY';
};

const totalsExpectedSign = (
  rows: ReportRow[],
  rowsById: Map<string, ReportRow>,
  isSectioned: boolean,
): ComparisonExpectedSign => {
  if (isSectioned) {
    const keys = new Set(rows.map((row) => findSection(row, rowsById)?.key).filter(Boolean));
    if (keys.size > 1) return 'POSITIVE';
    if (keys.has('EXPENSE')) return 'NEGATIVE';
    if (keys.has('INCOME')) return 'POSITIVE';
  }

  return unanimousExpectedSign(rows.map(rowExpectedSign));
};

/**
 * The rows the report's total is made of, in preference order.
 *
 * The grand-total row when the response sends one: it is the figure the grid's own total row shows,
 * so reading it means the header and the grid can never disagree by a rounding step or a row one of
 * them filtered.
 *
 * Otherwise the **root** rows, not the charted ones. On a sectioned report the roots are the
 * `INCOME` / `EXPENSE` wrappers, which already carry their subtree's sum — the same cells the grid
 * synthesizes its "Total Expenses" row from, since it reuses the section row's own `cellData`.
 * Summing the charted rows instead would total every section at once, which on a two-section report
 * read as income *plus* expenses where the grid showed expenses alone.
 */
const totalsContributors = (allRows: ReportRow[]): ReportRow[] => {
  const usable = allRows.filter((row) => !row.isExcludedFromReports);

  const totalRow = usable.find((row) => isTotalEntityRow(row) || row.isTotal);
  if (totalRow) return [totalRow];

  const byId = new Map(usable.map((row) => [String(row.id ?? ''), row]));
  return usable.filter((row) => !row.parentId || row.parentId === ROOT_PARENT_ID || !byId.has(row.parentId));
};

/**
 * The report's total for each range, from the selected column group.
 *
 * A range sums to `null` only when *every* contributing cell is absent; one absent row among several
 * present ones contributes nothing rather than voiding the total.
 */
const sumRangeValues = (
  contributors: ReportRow[],
  ranges: ComparisonChartRange[],
  fallbackMissingToZero = false,
): Array<number | null> =>
  ranges.map((range) => {
    let sum: number | null = fallbackMissingToZero ? 0 : null;
    let hasInvalidValue = false;
    contributors.forEach((row) => {
      const cell = findCellForColumn(row, range.columnId, true);
      const rawValue = cell?.value;
      const value = toNumber(rawValue);
      if (value == null) {
        if (fallbackMissingToZero && !isMissingNumber(rawValue)) hasInvalidValue = true;
        return;
      }
      sum = (sum ?? 0) + value;
    });
    return hasInvalidValue ? null : sum;
  });

/**
 * Use QCS delta cells when one real row owns the total; sums across multiple rows are synthetic and
 * must be derived from their summed period values instead.
 */
const deltaForContributors = (
  contributors: ReportRow[],
  values: Array<number | null>,
  expectedSign: ComparisonExpectedSign,
  deltaColumns: ComparisonDeltaColumns,
): ReturnType<typeof serverComparisonDelta> => {
  if (contributors.length !== 1 || (!deltaColumns.amount && !deltaColumns.percentage)) {
    return computeComparisonDelta(values[0] ?? null, values[COMPARISON_DELTA_RANGE_INDEX] ?? null, expectedSign);
  }

  const [row] = contributors;
  return serverComparisonDelta(
    values[0] ?? null,
    values[COMPARISON_DELTA_RANGE_INDEX] ?? null,
    readDedicatedDelta(row, deltaColumns.amount),
    readDedicatedDelta(row, deltaColumns.percentage),
    expectedSign,
    !deltaColumns.amount,
    !deltaColumns.percentage,
  );
};

const buildComparisonTotals = (
  allRows: ReportRow[],
  topRows: ReportRow[],
  rowsById: Map<string, ReportRow>,
  ranges: ComparisonChartRange[],
  isSectioned: boolean,
  sectionTotals: ComparisonChartSectionTotals[],
  isNetIncome: boolean,
  deltaColumns: ComparisonDeltaColumns,
  comparisonValueType: 'VALUE' | 'CUMULATIVE_VALUE',
): ComparisonChartTotals => {
  const contributors = totalsContributors(allRows);
  // A NET_INCOME response can carry the net as a third root alongside the INCOME and EXPENSE
  // wrappers. The grid hides that structural row's own cells and shows its child summary instead,
  // but summing roots would count (income + expenses) and then add the same net again. Its section
  // totals already select only the outermost income and expense wrappers, so combine those once.
  const values =
    isNetIncome && sectionTotals.length > 0
      ? ranges.map((_range, rangeIndex) => {
          const presentValues = sectionTotals
            .map((section) => section.values[rangeIndex])
            .filter((value): value is number => value != null);
          return presentValues.length > 0 ? presentValues.reduce((sum, value) => sum + value, 0) : null;
        })
      : sumRangeValues(contributors, ranges, isNetIncome && comparisonValueType === 'VALUE');
  const expectedSign = isNetIncome ? 'POSITIVE' : totalsExpectedSign(topRows, rowsById, isSectioned);
  // NET_INCOME combines section totals and is therefore always synthetic. Other one-row totals use
  // that row's dedicated QCS delta cells so the chart header matches the grid exactly.
  const delta =
    isNetIncome || comparisonValueType === 'CUMULATIVE_VALUE'
      ? computeComparisonDelta(values[0] ?? null, values[COMPARISON_DELTA_RANGE_INDEX] ?? null, expectedSign)
      : deltaForContributors(contributors, values, expectedSign, deltaColumns);

  return {
    values,
    expectedSign,
    deltaKind: delta.kind,
    deltaAmount: delta.amount,
    deltaAmountSigned: delta.signedAmount,
    deltaPercent: delta.percent,
  };
};

/**
 * The rows a section's total is made of: its **outermost** wrapper rows.
 *
 * A wrapper already carries its subtree's sum, so one row per section is normally the whole answer,
 * and it is the same cell the grid's "Total Expenses" row reads. Nested wrappers are excluded by
 * checking whether the parent resolves to the same section — that is what keeps a business-entitled
 * report from counting `EXPENSE_PERSONAL` and `EXPENSE_BUSINESS` on top of the `EXPENSE` that
 * already contains both. When no outer wrapper exists, those two are the outermost and both count.
 */
const sectionContributors = (
  allRows: ReportRow[],
  rowsById: Map<string, ReportRow>,
  key: ComparisonChartSectionKey,
): ReportRow[] =>
  allRows.filter((row) => {
    if (row.isExcludedFromReports || !isWrapperRow(row)) return false;
    if (findSection(row, rowsById)?.key !== key) return false;
    const parent = row.parentId ? rowsById.get(row.parentId) : undefined;
    return !parent || findSection(parent, rowsById)?.key !== key;
  });

/**
 * Income and expense totals for the header card, from the same selected column group as `totals`.
 *
 * Empty when the report is unsectioned: a payee report has one direction and no second block to
 * show, and inventing an "Income" block of zero would read as a measured figure.
 */
const buildComparisonSectionTotals = (
  allRows: ReportRow[],
  rowsById: Map<string, ReportRow>,
  ranges: ComparisonChartRange[],
  isSectioned: boolean,
  fallbackMissingToZero: boolean,
  deltaColumns: ComparisonDeltaColumns,
  comparisonValueType: 'VALUE' | 'CUMULATIVE_VALUE',
): ComparisonChartSectionTotals[] => {
  if (!isSectioned) return [];

  const order: Array<'INCOME' | 'EXPENSE'> = ['INCOME', 'EXPENSE'];

  return order.flatMap((key) => {
    const contributors = sectionContributors(allRows, rowsById, key);
    if (contributors.length === 0) return [];

    const values = sumRangeValues(contributors, ranges, fallbackMissingToZero && comparisonValueType === 'VALUE');
    const expectedSign: ComparisonExpectedSign = key === 'INCOME' ? 'POSITIVE' : 'NEGATIVE';
    const delta =
      comparisonValueType === 'CUMULATIVE_VALUE'
        ? computeComparisonDelta(values[0] ?? null, values[COMPARISON_DELTA_RANGE_INDEX] ?? null, expectedSign)
        : deltaForContributors(contributors, values, expectedSign, deltaColumns);

    return [
      {
        key,
        // A single outermost wrapper names the section; several mean no one name fits, so the
        // default wording stands rather than arbitrarily picking one of them.
        label:
          (contributors.length === 1 ? findSection(contributors[0], rowsById)?.label : undefined) ??
          DEFAULT_SECTION_LABELS[key],
        values,
        expectedSign,
        deltaKind: delta.kind,
        deltaAmount: delta.amount,
        deltaAmountSigned: delta.signedAmount,
        deltaPercent: delta.percent,
      },
    ];
  });
};

export const EMPTY_COMPARISON_CHART_DATA: ComparisonChartData = {
  isComparison: false,
  ranges: [],
  sections: [],
  totals: EMPTY_COMPARISON_CHART_TOTALS,
  sectionTotals: [],
  diagnostics: { reason: 'NOT_COMPARISON', usedUntypedColumnFallback: false },
};

/**
 * Everything the bar chart and the over-time totals share: the selected column group, one range per
 * series, the row indexes, and the header totals. Rows are left to `toComparisonChartData`, which is
 * the only caller that draws them.
 */
type ComparisonTotalsContext = {
  columns: ReportColumn[];
  selectedColumnGroupId?: string;
  usedUntypedColumnFallback: boolean;
  ranges: ComparisonChartRange[];
  deltaColumns: ComparisonDeltaColumns;
  allRows: ReportRow[];
  rowsById: Map<string, ReportRow>;
  childrenByParentId: Map<string, ReportRow[]>;
  rowType?: RowType;
  isNetIncome: boolean;
  isSectioned: boolean;
  topRows: ReportRow[];
  totals: ComparisonChartTotals;
  sectionTotals: ComparisonChartSectionTotals[];
};

const resolveComparisonTotals = (
  report: Report,
  comparisonColumnSource: ComparisonColumnSource,
  periodGroupId: string | undefined,
): { empty: ComparisonChartData } | { context: ComparisonTotalsContext } => {
  // A response with no typed column groups at all is not the grouped comparison shape this
  // understands — an older service or a flag mismatch. Chart the flat column list rather than
  // reporting the report empty while it holds data, mirroring `getGridColumns`' stance. A response
  // whose groups ARE typed but omit the selected source is genuinely malformed, and stays empty.
  const groups = getColumnGroups(report);
  const hasTypedGroups = groups.some((group) => group.type);
  const usedUntypedColumnFallback = groups.length > 0 && !hasTypedGroups;

  let columns: ReportColumn[];
  let selectedColumnGroupId: string | undefined;
  if (hasTypedGroups) {
    const sourceGroups = getComparisonColumnGroupsForSource(report, comparisonColumnSource);
    const selectedGroup =
      comparisonColumnSource === 'PERIOD' && periodGroupId
        ? sourceGroups.find((group) => group.groupId === periodGroupId)
        : sourceGroups[0];
    if (!selectedGroup) {
      const result = emptyData(comparisonColumnSource === 'PERIOD' ? 'NO_PERIOD_GROUP' : 'NO_TOTAL_GROUP');
      result.diagnostics.comparisonColumnSource = comparisonColumnSource;
      return { empty: result };
    }
    columns = selectedGroup.columns;
    // QCS can emit the single `NONE` bucket with only the primary value in PERIOD while placing
    // the comparison range in TOTAL. With exactly one period, TOTAL and PERIOD cover the same span,
    // so fill only ranges the period omitted. Never do this for multiple periods: a whole-range
    // total cannot stand in for an individual bucket.
    if (comparisonColumnSource === 'PERIOD' && sourceGroups.length === 1) {
      const selectedRangeIds = new Set(oneColumnPerRange(columns).map((column) => column.rangeId ?? PRIMARY_RANGE_ID));
      const totalColumns = getTotalColumnGroup(report)?.columns ?? [];
      const missingRangeIds = new Set(
        oneColumnPerRange(totalColumns)
          .map((column) => column.rangeId ?? PRIMARY_RANGE_ID)
          .filter((rangeId) => !selectedRangeIds.has(rangeId)),
      );
      if (missingRangeIds.size > 0) {
        columns = [
          ...columns,
          ...totalColumns.filter((column) => missingRangeIds.has(column.rangeId ?? PRIMARY_RANGE_ID)),
        ];
      }
    }
    selectedColumnGroupId = selectedGroup.groupId;
  } else {
    columns = report.columns ?? [];
  }

  const rangeHeaders = getComparisonRangeHeaders(report);

  const ranges: ComparisonChartRange[] = oneColumnPerRange(columns).map((column) => {
    const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
    const resolvedHeader = resolveComparisonRangeHeader(column, rangeHeaders);
    const configuredRange = report.configuration?.comparisonRanges?.find((range) => range.rangeId === rangeId);
    const configuredDates =
      rangeId === PRIMARY_RANGE_ID
        ? undefined
        : resolveComparisonRangeDates(
            report.startOn && report.endOn
              ? {
                  startOn: report.startOn,
                  endOn: report.endOn,
                  dateRange: report.configuration?.dateRange,
                  dateRangeInterval: report.configuration?.dateRangeInterval,
                  dateRangeIntervalCount: report.configuration?.dateRangeIntervalCount,
                }
              : undefined,
            configuredRange,
          );
    return {
      columnId: String(column.id ?? ''),
      rangeId,
      isPrimary: rangeId === PRIMARY_RANGE_ID,
      // Last resort is the group label ("Total", identical on every column in the group) rather than
      // a blank header, so a range the server named nowhere still reads as something.
      label: resolvedHeader.label || column.columnGroupLabel || '',
      startOn: resolvedHeader.startOn ?? configuredDates?.startOn,
      endOn: resolvedHeader.endOn ?? configuredDates?.endOn,
    };
  });

  if (ranges.length === 0) {
    const result = emptyData('NO_RANGES');
    result.diagnostics.comparisonColumnSource = comparisonColumnSource;
    result.diagnostics.columnGroupId = selectedColumnGroupId;
    return { empty: result };
  }

  const deltaColumns = findComparisonDeltaColumns(columns, ranges[COMPARISON_DELTA_RANGE_INDEX]?.rangeId);

  const allRows = report.rows ?? [];
  const rowsById = new Map<string, ReportRow>();
  const childrenByParentId = new Map<string, ReportRow[]>();
  allRows.forEach((row) => {
    if (row.id != null) rowsById.set(String(row.id), row);
    if (row.parentId != null) {
      const parentId = String(row.parentId);
      const siblings = childrenByParentId.get(parentId) ?? [];
      siblings.push(row);
      childrenByParentId.set(parentId, siblings);
    }
  });

  const rowType: RowType | undefined = report.configuration?.rowType;
  const isNetIncome = report.reportSubType === 'NET_INCOME';
  // Category reports have historically used the income/expense wrappers as chart sections. A
  // NET_INCOME report keeps those same top-level divisions for every grouping (account, payee,
  // tag, and so on), even though the rows inside each division change with `rowType`.
  const isSectioned = allRows.some(isWrapperRow) && (rowType === 'CATEGORY' || isNetIncome);

  const topRows = allRows.filter((row) => isChartableRow(row, rowsById));

  // Built from the top rows, never from `levelRows`: the header states the report's total, and
  // drilling into one category must not rewrite it to that category's.
  const sectionTotals = buildComparisonSectionTotals(
    allRows,
    rowsById,
    ranges,
    isSectioned,
    isNetIncome,
    deltaColumns,
    'VALUE',
  );
  const totals = buildComparisonTotals(
    allRows,
    topRows,
    rowsById,
    ranges,
    isSectioned,
    sectionTotals,
    isNetIncome,
    deltaColumns,
    'VALUE',
  );

  return {
    context: {
      columns,
      selectedColumnGroupId,
      usedUntypedColumnFallback,
      ranges,
      deltaColumns,
      allRows,
      rowsById,
      childrenByParentId,
      rowType,
      isNetIncome,
      isSectioned,
      topRows,
      totals,
      sectionTotals,
    },
  };
};

const EMPTY_COMPARISON_PERIOD_TOTALS: ComparisonPeriodTotals = {
  ranges: [],
  totals: EMPTY_COMPARISON_CHART_TOTALS,
  sectionTotals: [],
  cumulativeTotals: EMPTY_COMPARISON_CHART_TOTALS,
  cumulativeSectionTotals: [],
};

/**
 * One PERIOD group's additive and running header totals in a single pass, without building rows.
 *
 * Running totals are read only from each range's `CUMULATIVE_VALUE` column, which the saga requests
 * for every interval report and the grid hides. A range without one has no running total.
 * TODO(QCS-25542): once QCS returns `cumulativeValue` on VALUE cells, read it from there instead. Deltas
 * between running totals are always computed: the server's delta cells describe the period, not the
 * running total.
 */
export const toComparisonPeriodTotals = (
  report: Report | null | undefined,
  periodGroupId: string,
): ComparisonPeriodTotals => {
  if (!isComparisonReport(report) || !report) return EMPTY_COMPARISON_PERIOD_TOTALS;

  const resolved = resolveComparisonTotals(report, 'PERIOD', periodGroupId);
  if ('empty' in resolved) return EMPTY_COMPARISON_PERIOD_TOTALS;
  const { columns, ranges, totals, sectionTotals, allRows, topRows, rowsById, isSectioned, isNetIncome, deltaColumns } =
    resolved.context;

  const cumulativeColumnIdsByRange = new Map<string, string>();
  columns.forEach((column) => {
    const rangeId = column.rangeId ?? PRIMARY_RANGE_ID;
    if (column.comparisonColumnType !== 'CUMULATIVE_VALUE' || cumulativeColumnIdsByRange.has(rangeId)) return;
    cumulativeColumnIdsByRange.set(rangeId, String(column.id ?? ''));
  });
  // No cell has an empty column id, so a range without a cumulative column sums to null.
  const cumulativeRanges = ranges.map((range) => ({
    ...range,
    columnId: cumulativeColumnIdsByRange.get(range.rangeId) ?? '',
  }));
  const cumulativeSectionTotals = buildComparisonSectionTotals(
    allRows,
    rowsById,
    cumulativeRanges,
    isSectioned,
    isNetIncome,
    deltaColumns,
    'CUMULATIVE_VALUE',
  );
  const cumulativeTotals = buildComparisonTotals(
    allRows,
    topRows,
    rowsById,
    cumulativeRanges,
    isSectioned,
    cumulativeSectionTotals,
    isNetIncome,
    deltaColumns,
    'CUMULATIVE_VALUE',
  );

  return { ranges, totals, sectionTotals, cumulativeTotals, cumulativeSectionTotals };
};

/**
 * The comparison bar chart's whole data contract, derived from a generated report.
 *
 * Returns an empty-but-valid shape rather than throwing or returning `undefined` for every case it
 * cannot chart, with `diagnostics.reason` saying which — so the caller renders nothing without
 * needing to know why, and a developer can find out.
 */
export const toComparisonChartData = (
  report: Report | null | undefined,
  {
    breadcrumbs = EMPTY_CRUMBS,
    comparisonColumnSource = DEFAULT_COMPARISON_COLUMN_SOURCE,
    periodGroupId,
  }: ToComparisonChartDataOptions = {},
): ComparisonChartData => {
  if (!isComparisonReport(report) || !report) {
    return EMPTY_COMPARISON_CHART_DATA;
  }

  const resolved = resolveComparisonTotals(report, comparisonColumnSource, periodGroupId);
  if ('empty' in resolved) return resolved.empty;
  const {
    selectedColumnGroupId,
    usedUntypedColumnFallback,
    ranges,
    deltaColumns,
    rowsById,
    childrenByParentId,
    rowType,
    isNetIncome,
    isSectioned,
    topRows,
    totals,
    sectionTotals,
  } = resolved.context;

  type Bucket = {
    key: ComparisonChartSectionKey;
    label: string;
    expectedSign: ComparisonExpectedSign;
    rows: ComparisonChartRow[];
  };
  const buckets = new Map<ComparisonChartSectionKey, Bucket>();

  const bucketFor = (assignment: SectionAssignment): Bucket => {
    const existing = buckets.get(assignment.key);
    if (existing) return existing;
    const created: Bucket = { ...assignment, rows: [] };
    buckets.set(assignment.key, created);
    return created;
  };

  // The level the breadcrumbs point at — the top level when there are none.
  const levelRows = resolveLevelRows(topRows, childrenByParentId, breadcrumbs);
  levelRows.forEach((row) => {
    const assignment: SectionAssignment | undefined = isSectioned
      ? findSection(row, rowsById)
      : { key: 'SINGLE', label: '', expectedSign: rowExpectedSign(row) };

    // A sectioned report with a row that belongs to neither wrapper has nowhere to put it, and
    // guessing a section would put it on the wrong scale.
    if (!assignment) return;

    const expectedSign = isSectioned ? assignment.expectedSign : rowExpectedSign(row);
    const hasChildren = !!row.id && (childrenByParentId.get(String(row.id))?.length ?? 0) > 0;
    const cellsByColumnId = new Map((row.cellData ?? []).map((cell) => [cell.columnId, cell]));

    const values: ComparisonChartValue[] = ranges.map((range) => {
      const cell = cellsByColumnId.get(range.columnId);
      const rawValue = cell?.value;
      const parsedValue = toNumber(rawValue);
      return {
        columnId: range.columnId,
        rangeId: range.rangeId,
        // Match the grid: NET_INCOME treats an omitted leaf/subtotal value as zero, but structural
        // parents stay blank. A supplied non-numeric value is malformed, not an omitted amount.
        value: parsedValue ?? (isNetIncome && !hasChildren && isMissingNumber(rawValue) ? 0 : null),
        serverDeltaAmount: toServerNumber(cell?.deltaAmount),
        serverDeltaPercentage: toServerNumber(cell?.deltaPercentage),
      };
    });

    // Dedicated delta columns are authoritative for display. Older payloads nested those values on
    // the comparison VALUE cell, so that shape remains a fallback only when the dedicated column
    // itself is absent. Direction is always derived from the two period values.
    const deltaCell = values[COMPARISON_DELTA_RANGE_INDEX];
    const serverAmount = deltaColumns.amount
      ? readDedicatedDelta(row, deltaColumns.amount)
      : deltaCell?.serverDeltaAmount;
    const serverPercent = deltaColumns.percentage
      ? readDedicatedDelta(row, deltaColumns.percentage)
      : deltaCell?.serverDeltaPercentage;
    const delta = serverComparisonDelta(
      values[0]?.value ?? null,
      deltaCell?.value ?? null,
      serverAmount,
      serverPercent,
      expectedSign,
      !deltaColumns.amount,
      !deltaColumns.percentage,
    );

    bucketFor(assignment).rows.push({
      id: String(row.id ?? ''),
      parentId: row.parentId ? String(row.parentId) : undefined,
      entityId: row.entityId,
      hasChildren,
      name: row.name ?? '',
      names: buildRowNames(row, rowsById),
      expectedSign,
      values,
      deltaKind: delta.kind,
      deltaAmount: delta.amount,
      deltaAmountSigned: delta.signedAmount,
      deltaPercent: delta.percent,
    });
  });

  // Personal and business account groups can have the same server-provided name. Qualify both
  // only when chart-visible rows collide across ledgers, keeping the usual compact label when a
  // same-name row has no activity and will be removed by the presentation layer.
  if (rowType === 'ACCOUNT') {
    buckets.forEach((bucket) => {
      const rowsByNameAndUsage = bucket.rows.reduce<Map<string, Map<AccountUsageLabel, ComparisonChartRow[]>>>(
        (groupedRows, chartRow) => {
          if (!chartRow.values.some(({ value }) => value != null && value !== 0)) return groupedRows;

          const sourceRow = rowsById.get(chartRow.id);
          if (!sourceRow) return groupedRows;
          const usage = findAccountUsageLabel(sourceRow, rowsById);
          if (!usage) return groupedRows;
          const rowsByUsage = groupedRows.get(chartRow.name) ?? new Map<AccountUsageLabel, ComparisonChartRow[]>();
          const matchingRows = rowsByUsage.get(usage) ?? [];
          matchingRows.push(chartRow);
          rowsByUsage.set(usage, matchingRows);
          groupedRows.set(chartRow.name, rowsByUsage);
          return groupedRows;
        },
        new Map(),
      );

      rowsByNameAndUsage.forEach((rowsByUsage) => {
        if (rowsByUsage.size < 2) return;
        rowsByUsage.forEach((matchingRows, usage) => {
          matchingRows.forEach((chartRow) => {
            chartRow.name = `${chartRow.name}\n(${usage})`;
          });
        });
      });
    });
  }

  // INCOME before EXPENSE, matching the grid and the mock. `SINGLE` is the only bucket when unsectioned.
  const order: ComparisonChartSectionKey[] = ['INCOME', 'EXPENSE', 'SINGLE'];
  const sections: ComparisonChartSection[] = order
    .map((key) => buckets.get(key))
    .filter((bucket): bucket is Bucket => !!bucket && bucket.rows.length > 0)
    .map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      // An unsectioned payee/account report can mix income and expense rows. In that case there is
      // no one semantic direction for its totals, so preserve raw arithmetic signs with `ANY`.
      expectedSign:
        bucket.key === 'SINGLE'
          ? unanimousExpectedSign(bucket.rows.map((row) => row.expectedSign))
          : bucket.expectedSign,
      rows: bucket.rows,
      maxAbsValue: bucket.rows.reduce(
        (max, row) => row.values.reduce((rowMax, value) => Math.max(rowMax, Math.abs(value.value ?? 0)), max),
        0,
      ),
    }));

  if (sections.length === 0) {
    return {
      ...emptyData('NO_ROWS'),
      ranges,
      totals,
      sectionTotals,
      diagnostics: {
        reason: 'NO_ROWS',
        usedUntypedColumnFallback,
        comparisonColumnSource,
        columnGroupId: selectedColumnGroupId,
      },
    };
  }

  return {
    isComparison: true,
    ranges,
    sections,
    totals,
    sectionTotals,
    diagnostics: { usedUntypedColumnFallback, comparisonColumnSource, columnGroupId: selectedColumnGroupId },
  };
};
